import { useState } from "react";
import type { Input } from '@/features/inventory/services/inputsApi';
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import {
  useBatchesByInput,
  useCreateBatch,
  useDeleteBatch,
  useUpdateBatch,
} from "../../hooks/useBatches";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input as InputComponent } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { Boxes } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface BatchesDialogProps {
  input: Input;
}

export const BatchesDialog = ({ input }: BatchesDialogProps) => {
  const initialBatchData = {
    batchNumber: "",
    lotNumber: "",
    receivedQuantity: 0,
    receivedDate: new Date().toISOString().split("T")[0],
    manufactureDate: "",
    expiryDate: "",
    unitCost: input.unitCost,
    purchaseOrderNumber: "",
    invoiceNumber: "",
  };
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(initialBatchData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const batchesQuery = useBatchesByInput(input.id);
  const createMutation = useCreateBatch(input.id);
  const updateMutation = useUpdateBatch(input.id);
  const deleteMutation = useDeleteBatch(input.id);
  const confirm = useConfirm();

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const payload = {
      ...formData,
      inputId: input.id,
      receivedDate: new Date(formData.receivedDate).toISOString(),
      manufactureDate: new Date(formData.manufactureDate).toISOString(),
      expiryDate: new Date(formData.expiryDate).toISOString(),
    };

    createMutation.mutate(payload as any, {
      onSuccess: () => {
        setFormData(initialBatchData);
        setIsCreating(false);
      },
      onError: (error: any) => {
        if (error.response?.data?.errors) {
          setFieldErrors(error.response.data.errors);
        }
      },
    });
  };

  const handleDelete = (id: string) => {
    confirm({
      title: "Delete batch?",
      description: "This batch will be permanently removed. This can't be undone.",
      variant: "destructive",
      confirmLabel: "Delete",
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), "Batch deleted"),
    });
  };

  const isExpired = (expiryDate: string) => {
    return new Date(expiryDate) < new Date();
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diff = expiry.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 3600 * 24));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Boxes className="w-4 h-4" />
          View Batches
        </button>
      </DialogTrigger>
      <DialogContent
        
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]"
      >
        <DialogHeader>
          <DialogTitle>Batches</DialogTitle>
          <DialogDescription>
            Manage batches for {input.inputName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Batches */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Batches</h3>
              {/* {!isCreating && (
                <Button
                  size="sm"
                  onClick={() => setIsCreating(true)}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  New Batch
                </Button>
              )} */}
            </div>

            {batchesQuery.isLoading ? (
              <LoadingAnimation text="Loading batches..." />
            ) : batchesQuery.data && batchesQuery.data.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch Number</TableHead>
                      <TableHead>Lot Number</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Unit Cost</TableHead>
                      <TableHead>PO #</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Received</TableHead>
                      <TableHead>Expiry</TableHead>
                      <TableHead>Approval</TableHead>
                      <TableHead>Status</TableHead>
                      {/* <TableHead className="text-right">Actions</TableHead> */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batchesQuery.data.map((batch) => {
                      const daysLeft = getDaysUntilExpiry(batch.expiryDate);
                      const expired = isExpired(batch.expiryDate);

                      const pending = batch.approvalStatus === "PendingApproval";
                      const rejected = batch.approvalStatus === "Rejected";

                      return (
                        <TableRow
                          key={batch.id}
                          className={
                            rejected
                              ? "bg-red-50 opacity-70"
                              : pending
                                ? "bg-amber-50"
                                : expired
                                  ? "bg-red-50"
                                  : daysLeft <= 30
                                    ? "bg-yellow-50"
                                    : ""
                          }
                        >
                          <TableCell className="font-medium">
                            {batch.batchNumber}
                          </TableCell>
                          <TableCell className="text-sm">
                            {batch.lotNumber}
                          </TableCell>
                          <TableCell className="text-sm">
                            {batch.receivedQuantity} {input.unitOfMeasure}
                          </TableCell>
                          <TableCell className="text-sm">
                            KES {batch.unitCost.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm">
                            {batch.purchaseOrderNumber || '—'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {batch.invoiceNumber || '—'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(batch.receivedDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(batch.expiryDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {pending ? (
                              <Badge variant="outline" className="bg-amber-100 border-amber-300 text-amber-900">
                                Pending GM
                              </Badge>
                            ) : rejected ? (
                              <Badge variant="destructive" title={batch.rejectionReason ?? undefined}>
                                Rejected
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-50">
                                Approved
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {expired ? (
                              <Badge variant="destructive">Expired</Badge>
                            ) : daysLeft <= 30 ? (
                              <Badge variant="outline" className="bg-yellow-50">
                                {daysLeft} days left
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-50">
                                Good
                              </Badge>
                            )}
                          </TableCell>
                          {/* <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(batch.id)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </TableCell> */}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 border rounded-lg border-dashed">
                No batches for this input
              </div>
            )}
          </div>

          {/* Create Form */}
          {isCreating && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-4">Add New Batch</h4>
              <form onSubmit={handleSubmit} className="space-y-4">
                {createMutation.error && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                    {createMutation.error.message}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  <Field>
                    <FieldLabel htmlFor="batchNumber">
                      Batch Number <span className="text-red-500">*</span>
                    </FieldLabel>
                    <InputComponent
                      id="batchNumber"
                      value={formData.batchNumber}
                      onChange={(e) =>
                        handleChange("batchNumber", e.target.value)
                      }
                      placeholder="e.g., BATCH-001"
                      required
                      disabled={createMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="lotNumber">
                      Lot Number <span className="text-red-500">*</span>
                    </FieldLabel>
                    <InputComponent
                      id="lotNumber"
                      value={formData.lotNumber}
                      onChange={(e) =>
                        handleChange("lotNumber", e.target.value)
                      }
                      placeholder="e.g., LOT-2026-001"
                      required
                      disabled={createMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="receivedQuantity">
                      Received Quantity <span className="text-red-500">*</span>
                    </FieldLabel>
                    <InputComponent
                      id="receivedQuantity"
                      type="number"
                      step="0.01"
                      value={formData.receivedQuantity}
                      onChange={(e) =>
                        handleChange(
                          "receivedQuantity",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      required
                      disabled={createMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="unitCost">
                      Unit Cost <span className="text-red-500">*</span>
                    </FieldLabel>
                    <InputComponent
                      id="unitCost"
                      type="number"
                      step="0.01"
                      value={formData.unitCost}
                      disabled={true} // Read-only from input
                      className="bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      From Input: KES {input.unitCost.toLocaleString()}
                    </p>
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="receivedDate">
                      Received Date <span className="text-red-500">*</span>
                    </FieldLabel>
                    <DatePicker
                      id="receivedDate"
                      value={formData.receivedDate}
                      onChange={(value) => handleChange("receivedDate", value)}
                      required
                      disabled={createMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="manufactureDate">
                      Manufacture Date
                    </FieldLabel>
                    <DatePicker
                      id="manufactureDate"
                      value={formData.manufactureDate}
                      onChange={(value) => handleChange("manufactureDate", value)}
                      disabled={createMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="expiryDate">
                      Expiry Date <span className="text-red-500">*</span>
                    </FieldLabel>
                    <DatePicker
                      id="expiryDate"
                      value={formData.expiryDate}
                      onChange={(value) => handleChange("expiryDate", value)}
                      required
                      disabled={createMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="purchaseOrderNumber">
                      PO Number
                    </FieldLabel>
                    <InputComponent
                      id="purchaseOrderNumber"
                      value={formData.purchaseOrderNumber}
                      onChange={(e) =>
                        handleChange("purchaseOrderNumber", e.target.value)
                      }
                      placeholder="e.g., PO-2026-001"
                      disabled={createMutation.isPending}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="invoiceNumber">
                      Invoice Number
                    </FieldLabel>
                    <InputComponent
                      id="invoiceNumber"
                      value={formData.invoiceNumber}
                      onChange={(e) =>
                        handleChange("invoiceNumber", e.target.value)
                      }
                      placeholder="e.g., INV-2026-001"
                      disabled={createMutation.isPending}
                    />
                  </Field>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="flex-1"
                  >
                    {createMutation.isPending ? "Creating..." : "Create Batch"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreating(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
